import { useWorkspaceId } from "@sg-protect/shell";
import { useMemo } from "react";
import { useParams } from "react-router";

export const workspacePaths = {
    root: "/migrate/:workspaceId",
    migrationProject: "/migrate/:workspaceId/project",
    migrationProjectOverview: "/migrate/:workspaceId/project/overview",
    migrationProjectIdentities: "/migrate/:workspaceId/project/identities",
    migrationProjectOneDrive: "/migrate/:workspaceId/project/one-drive",
    migrationProjectSharePoint: "/migrate/:workspaceId/project/sharepoint",
    migrationProjectTeams: "/migrate/:workspaceId/project/teams"
} as const;

const projectPaths = {
    root: "/migrate/:workspaceId",
    migrationProject: "/migrate/:workspaceId/project/:projectId",
    migrationProjectOverview: "/migrate/:workspaceId/project/:projectId/overview",
    migrationProjectIdentities: "/migrate/:workspaceId/project/:projectId/identities",
    migrationProjectOneDrive: "/migrate/:workspaceId/project/:projectId/one-drive",
    migrationProjectSharePoint: "/migrate/:workspaceId/project/:projectId/sharepoint",
    migrationProjectTeams: "/migrate/:workspaceId/project/:projectId/teams",
    migrationProjectPlan: "/migrate/:workspaceId/project/:projectId/plan",
    migrationProjectActivity: "/migrate/:workspaceId/project/:projectId/activity"
} as const;

interface PathParams {
    workspaceId?: string;
    projectId?: string;
}

interface GetPathsOptions {
    workspaceId?: string;
}

interface UsePathsOptions {
    prefillWorkspaceId?: boolean;
}

function prefillPaths<const T extends Record<string, string>>(paths: T, params: PathParams): T {
    return Object.fromEntries(
        Object.entries(paths).map(([key, value]) => {
            let path = value;
            if (params.workspaceId !== undefined) {
                path = path.replaceAll(":workspaceId", params.workspaceId);
            }
            if (params.projectId !== undefined) {
                path = path.replaceAll(":projectId", params.projectId);
            }

            return [key, path];
        })
    ) as T;
}

export function getPaths(options?: GetPathsOptions) {
    return options?.workspaceId ? prefillPaths(workspacePaths, { workspaceId: options.workspaceId }) : workspacePaths;
}

export function getProjectPaths(params: PathParams) {
    return prefillPaths(projectPaths, params);
}

export function usePaths(options?: UsePathsOptions) {
    const workspaceId = useWorkspaceId();
    const { projectId } = useParams();

    return useMemo(() => {
        const paths = projectId !== undefined ? projectPaths : workspacePaths;

        return options?.prefillWorkspaceId === false ? paths : prefillPaths(paths, { workspaceId, projectId });
    }, [options?.prefillWorkspaceId, workspaceId, projectId]);
}
